import { createSelector } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/query';

import type { RootState } from 'store';

import type { Option } from 'shared/models/common';
import type {
  CaseLevel1Option,
  CaseLevel2Option,
} from 'shared/models/common-cases';
import { CaseLevel1Code } from 'shared/constants/common-cases';
import searchBy from 'shared/utils/searchBy';
import formatDate from 'shared/utils/formatDate';
import processTableFilterOptions from 'shared/utils/processTableFilterOptions';

import { userCommonCasesApi } from './api';
import type { UserCommonCase } from './types';

const selectSelf = (state: RootState) => state;

const emptyCaseLevels1: CaseLevel1Option[] = [];
const emptyCaseLevels2: CaseLevel2Option[] = [];
const emptyUserCommonCasesList: UserCommonCase[] = [];

const mapValuesToArray = <K, V>(map: Map<K, V>) => {
  return Array.from(map.values());
};

const setToArray = <V>(set: Set<V>) => Array.from(set);

const selectResponse = createSelector(selectSelf, (state) => {
  const { current_cooperative } = state.settings;

  return userCommonCasesApi.endpoints.getCommonCases.select(
    current_cooperative ?? skipToken
  )(state);
});

export const selectDefaultCaseLevel1 = createSelector(
  selectSelf,
  (state) => state.userCommonCases.caseLevel1
);

export const selectDefaultCaseLevel2 = createSelector(
  selectSelf,
  (state) => state.userCommonCases.caseLevel2
);

export const selectCaseLevels1 = createSelector(
  selectResponse,
  (response) => response.data?.CaseLevels1 ?? emptyCaseLevels1
);

export const selectCaseLevels2 = createSelector(
  selectResponse,
  (response) => response.data?.CaseLevels2 ?? emptyCaseLevels2
);

export const selectUserCommonCasesList = createSelector(
  selectResponse,
  (response) => response.data?.Cases ?? emptyUserCommonCasesList
);

export const selectSearchTerm = createSelector(
  selectSelf,
  (state) => state.userCommonCases.searchTerm
);

export const selectAvailableOptions = createSelector(
  selectUserCommonCasesList,
  (cases) => {
    const caseLevel1Cache = new Map<CaseLevel1Code, CaseLevel1Option>();
    const statusesCache = new Map<number, Option>();
    const createdOnYearsCache = new Set<number>();

    for (const caseRecord of cases) {
      if (caseRecord.CaseLevel1)
        caseLevel1Cache.set(
          caseRecord.CaseLevel1?.Value,
          caseRecord.CaseLevel1
        );

      if (caseRecord.Status)
        statusesCache.set(caseRecord.Status?.Value, caseRecord.Status);

      if (caseRecord.CreatedOn)
        createdOnYearsCache.add(new Date(caseRecord.CreatedOn).getFullYear());
    }

    return {
      caseLevel1: processTableFilterOptions(mapValuesToArray(caseLevel1Cache)),
      createdOnYear: processTableFilterOptions(
        setToArray(createdOnYearsCache),
        { sortOrder: 'desc' }
      ),
      status: processTableFilterOptions(mapValuesToArray(statusesCache)),
    };
  }
);

export const selectFilteredBySearchTermUserCommonCasesList = createSelector(
  selectUserCommonCasesList,
  selectSearchTerm,
  (casesList, searchTerm) =>
    searchBy(
      casesList,
      [
        (caseItem) => caseItem.Title ?? '',
        (caseItem) => caseItem.Space?.Name ?? '',
        (caseItem) => caseItem.CaseLevel1?.Label ?? '',
        (caseItem) => caseItem.Status?.Label ?? '',
        (caseItem) => caseItem.Description ?? '',
        (caseItem) =>
          caseItem.CreatedOn ? formatDate(new Date(caseItem.CreatedOn)) : '',
        (caseItem) =>
          caseItem.ModifiedOn ? formatDate(new Date(caseItem.ModifiedOn)) : '',
      ],
      searchTerm
    )
);
